import { isNonEmpty } from '@sgme/fp';
import type { Action } from 'state/actions';
import { QuoteState } from 'state/quotes/infos/infos.model';
import type { FormInfosStateMap, StaticInfos } from 'state/static/infos/infos.model';
import { addKey, addOrPatchKey, addOrReplaceKey, patchKey, removeKey } from 'state/utils/collection';
import { addRejectionError, removeRejectionError } from 'state/utils/rejectionError';
import { dateNow } from 'utils/core/now';

export function infosReducer(state: FormInfosStateMap = {}, action: Action): FormInfosStateMap {
  switch (action.type) {
    case 'WORKSPACE_RESET_TAB_REQUESTED':
      return removeKey(state, action.tabId);
    case 'DEFAULT_FORM_RECEIVED': {
      if (action.workspace !== 'static') {
        return state;
      }
      const {
        tabId,
        productView,
        productDescription,
        version,
        negotiationId,
        actions,
        quoteQuery,
        remunerationMode,
        mustForceAdvancedMode,
        validationErrors,
        sendWholeFormData,
        instrumentName,
      } = action;
      return addKey(state, tabId, <StaticInfos>{
        actions,
        negotiationId,
        productDescription,
        instrumentName,
        productView,
        quoteQuery,
        version,
        remunerationMode,
        mustForceAdvancedMode,
        validationErrors: isNonEmpty(validationErrors) ? validationErrors : undefined,
        sendWholeFormData,
      });
    }

    case 'FORM_ENRICHMENT_REQUESTED':
      if (action.workspace !== 'static') {
        return state;
      }
      return patchKey(state, action.tabId, () => ({ enrichmentError: undefined }));
    case 'FORM_ENRICHMENT_RECEIVED': {
      if (action.workspace !== 'static') {
        return state;
      }
      const {
        tabId,
        productDescription,
        instrumentName,
        version,
        actions,
        quoteQuery,
        validationErrors,
        remunerationMode,
        mustForceAdvancedMode,
      } = action;
      return patchKey(state, tabId, info => ({
        ...info,
        productDescription,
        version,
        instrumentName,
        actions,
        quoteQuery,
        validationErrors: isNonEmpty(validationErrors) ? validationErrors : undefined,
        remunerationMode,
        technicalWarning: undefined,
        mustForceAdvancedMode,
      }));
    }
    case 'FORM_RELOADED': {
      if (action.workspace !== 'static') {
        return state;
      }
      const { tabId, type: _t, workspace: _w, ...payload } = action;
      return addOrReplaceKey<StaticInfos>(state, tabId, payload);
    }
    case 'FORM_ENRICHMENT_FAILED':
      if (action.workspace !== 'static') {
        return state;
      }
      return patchKey(state, action.tabId, () => ({ enrichmentError: action.error }));
    case 'QUOTE_ACKED':
      if (action.workspace !== 'static') {
        return state;
      }
      return patchKey(state, action.tabId, () => ({
        quoteId: action.quoteId,
        quoteState: 'Live' as QuoteState,
        version: action.newVersion,
      }));
    case 'PREVIOUS_QUOTE_LOAD_REQUESTED':
      if (action.workspace !== 'static') {
        return state;
      }
      return addOrPatchKey(state, action.tabId, () => ({
        quoteId: action.quoteId,
        quoteState: 'Previous' as QuoteState,
      }));
    case 'STREAMING_QUOTE_ACKED':
      if (action.workspace !== 'static') {
        return state;
      }
      return patchKey(state, action.tabId, () => ({
        quoteId: action.quoteId,
        quoteState: 'Live' as QuoteState,
      }));
    case 'PREVIOUS_QUOTE_LOADING_FAILED':
      if (action.workspace !== 'static') {
        return state;
      }
      return addOrPatchKey(state, action.tabId, () => ({
        quoteId: undefined,
        quoteState: undefined,
      }));
    case 'QUOTE_STALE':
      if (action.workspace !== 'static') {
        return state;
      }
      return patchKey(state, action.tabId, () => ({
        quoteState: action.hasFormChanged ? ('Inconsistent' as QuoteState) : ('Previous' as QuoteState),
      }));
    case 'TRADE_AUTH_RECEIVED': {
      if (action.workspace !== 'static') {
        return state;
      }
      const tradeAuthCreationDate = action.when.getTime();
      const now = dateNow();
      const isPastQuote = tradeAuthCreationDate + action.expirationDelayInSeconds * 1000 < now;
      const tradeAuthGranted = action.status === 'Granted';

      if (!tradeAuthGranted) {
        return addOrPatchKey(state, action.tabId, () => ({
          quoteId: action.quoteId,
          quoteState: 'Live' as QuoteState,
        }));
      }
      return addOrPatchKey(state, action.tabId, () => ({
        quoteId: action.quoteId,
        quoteState: isPastQuote ? 'Previous' : ('Live' as QuoteState),
      }));
    }
    case 'QUOTE_TRADE_REJECTED': {
      if (action.workspace !== 'static') {
        return state;
      }
      return addOrPatchKey(state, action.tabId, () => ({
        quoteId: action.quoteId,
        quoteState: 'Live' as QuoteState,
      }));
    }
    case 'UPDATE_RFQ_VERSION':
      if (action.workspace !== 'static') {
        return state;
      }
      return patchKey(state, action.tabId, () => ({
        version: action.newVersion,
      }));

    case 'WORKSPACE_TAB_REMOVED':
      return removeKey(state, action.id);

    case 'QUOTE_REMOVED':
      if (action.workspace !== 'static') {
        return state;
      }
      return patchKey(state, action.tabId, infos => {
        if (infos.quoteId !== action.quoteId) {
          return infos; // message is specific to an old quote
        }
        return {
          quoteId: undefined,
          quoteState: undefined,
        };
      });

    case 'PRODUCT_SUMMARY_RECEIVED':
      return patchKey(state, action.tabId, () => ({
        summary: { payload: action.payload, opened: true },
      }));

    case 'PRODUCT_SUMMARY_CLOSED':
      return patchKey(state, action.tabId, () => ({
        summary: { opened: false },
      }));

    case 'FORM_ENRICHMENT_ERROR_DISCARDED':
      if (action.workspace !== 'static') {
        return state;
      }
      return patchKey(state, action.tabId, () => {
        return {
          enrichmentError: undefined,
          crossBrowserDirty: undefined,
        };
      });
    case 'FORM_TAB_ROLLBACKED':
      if (action.workspace !== 'static') {
        return state;
      }
      return patchKey(state, action.tabId, () => {
        return {
          enrichmentError: undefined,
          crossBrowserDirty: undefined,
        };
      });

    case 'QUOTE_ACTIVE_FORM_TAB_CHANGED':
      if (action.workspace !== 'static') {
        return state;
      }
      return patchKey(state, action.tabId, () => ({
        formActiveTab: action.formTabId,
      }));
    case 'STATIC_SESSION_MODIFIED':
      return patchKey(state, action.tabId, () => ({
        crossBrowserDirty: true,
      }));
    case 'QUOTE_TRADE_REQUEST_REJECTED':
      if (action.workspace !== 'static') {
        return state;
      }
      return patchKey(state, action.tabId, previousState => ({
        validationErrors: addRejectionError(previousState.validationErrors ?? [], action.error),
      }));
    case 'QUOTE_TRADE_REQUESTED':
      if (action.workspace !== 'static') {
        return state;
      }
      return patchKey(state, action.tabId, previousState => ({
        validationErrors: removeRejectionError(previousState.validationErrors ?? []),
      }));
    case 'STREAMING_QUOTE_CLOSED':
    case 'STREAMING_QUOTE_EXPIRED':
      return action.workspace === 'static'
        ? patchKey(state, action.tabId, () => ({
            documentation: undefined,
            quoteState: 'Previous' as QuoteState,
          }))
        : state;
    case 'STREAMING_TRADE_REQUESTED':
      if (action.workspace !== 'static') {
        return state;
      }
      return patchKey(state, action.tabId, previousState => ({
        validationErrors: removeRejectionError(previousState.validationErrors ?? []),
      }));
    case 'FORM_DOCUMENTATION_V3_RECEIVED':
      if (action.workspace !== 'static') {
        return state;
      }
      const { tabId, documentation } = action;
      return patchKey(state, tabId, info => ({
        ...info,
        documentation: {
          docs: documentation.docs,
        },
      }));
    default:
      return state;
  }
}
